/**
 * Rewrite Supabase `/object/public/` delivery URLs to `/render/image/public/`
 * so width/quality/format transforms are accepted. Non-Supabase and signed
 * object URLs are left unchanged.
 */
export function toSupabaseTransformableUrl(url: string): string {
  if (!url || typeof url !== "string") return url;
  if (url.includes("/storage/v1/render/image/")) return url;
  if (url.includes("/storage/v1/object/public/")) {
    return url.replace(
      "/storage/v1/object/public/",
      "/storage/v1/render/image/public/",
    );
  }
  return url;
}

/**
 * Helper function to convert Supabase image URLs to WebP format (and optional
 * display-sized resize). Rewrites `/object/public/` → `/render/image/public/`
 * first; other hosts are returned unchanged.
 *
 * @param url - The original image URL
 * @param width - Optional width for resizing
 * @param height - Optional height for resizing
 * @param quality - Image quality (1-100), defaults to 85
 * @returns Transformed URL with WebP format parameters, or original URL if not applicable
 */
export const convertToWebP = (
  url: string,
  width?: number,
  height?: number,
  quality: number = 85,
  /**
   * Supabase resize mode. For reel/story thumbs, `width` alone can return a
   * non-proportional buffer (e.g. 360×2048) — pass height + `cover`/`contain`.
   */
  resize?: "cover" | "contain" | "fill",
): string => {
  if (!url || typeof url !== "string") return url;

  const transformable = toSupabaseTransformableUrl(url);
  // Only the /render/image/ Supabase endpoint supports transformation params.
  if (!transformable.includes("/storage/v1/render/image/")) return url;

  // Build transformation parameters
  const transformParams = new URLSearchParams();
  transformParams.append("format", "webp");
  transformParams.append("quality", quality.toString());

  if (width) transformParams.append("width", width.toString());
  // Height is optional; omit when resizing banners to preserve aspect ratio.
  if (height) transformParams.append("height", height.toString());
  // Width-only without resize returns a broken buffer on Supabase render
  // (keeps original height, e.g. 640×2048 or 640×1707). Default to contain
  // so aspect is preserved; callers that need a crop pass height + cover.
  const resolvedResize =
    resize ?? (width && !height ? "contain" : undefined);
  if (resolvedResize) transformParams.append("resize", resolvedResize);

  const separator = transformable.includes("?") ? "&" : "?";
  return `${transformable}${separator}${transformParams.toString()}`;
};

/** Display-sized delivery for above-fold template media (hero / about / avatar). */
export function getTemplateImageSrc(
  url: string,
  width = 900,
  quality = 75,
): string {
  return convertToWebP(url, width, undefined, quality);
}

function parseCssAspectRatio(aspectRatio: string): number | null {
  if (!aspectRatio || aspectRatio === "auto") return null;
  const parts = aspectRatio.split("/").map((p) => parseFloat(p.trim()));
  if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
    return parts[0] / parts[1];
  }
  if (parts.length === 1 && parts[0] > 0) return parts[0];
  return null;
}

/** SVG placeholder that keeps the box and shows a simple broken-state label. */
function brokenImagePlaceholderSrc(width: number, height: number): string {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const fontSize = Math.max(11, Math.min(14, Math.round(Math.min(w, h) / 12)));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <rect width="100%" height="100%" fill="#f0f0f0"/>
  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
    fill="#6b6b6b" font-family="system-ui,-apple-system,sans-serif"
    font-size="${fontSize}">Image unavailable</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Safari ignores CSS `aspect-ratio` / `height: auto` on failed &lt;img&gt; loads and
 * sizes from the broken-image icon instead. Lock the box from computed styles
 * (aspect-ratio, square size, etc.), then swap to a placeholder that shows
 * "Image unavailable".
 *
 * Use as `onError={preserveBrokenImageBox}` on any feed / card image.
 */
export const preserveBrokenImageBox = (event: {
  currentTarget: HTMLImageElement;
}): void => {
  const img = event.currentTarget;
  if (!img || img.src.startsWith("data:image/svg+xml")) return;

  const cs = window.getComputedStyle(img);
  const rect = img.getBoundingClientRect();

  let width = rect.width || img.clientWidth || parseFloat(cs.width) || 0;
  let height = rect.height || img.clientHeight || parseFloat(cs.height) || 0;
  if (!width || !height) {
    const parent = img.parentElement?.getBoundingClientRect();
    if (!width) width = parent?.width || 0;
    if (!height) height = parent?.height || 0;
  }

  const ratio = parseCssAspectRatio(cs.aspectRatio);
  if (width > 0 && ratio) {
    const expectedHeight = width / ratio;
    // Safari broken icons often report a useless height — prefer aspect-ratio.
    if (!height || Math.abs(height - expectedHeight) > 1) {
      height = expectedHeight;
    }
  }

  if (img.classList.contains("square-image")) {
    const sqW = parseFloat(cs.width);
    const sqH = parseFloat(cs.height);
    if (sqW > 0) width = sqW;
    if (sqH > 0) height = sqH;
    else if (width > 0) height = width;
  }

  if (!width) width = 175;
  if (!height) height = Math.round(width * (290 / 175));
  img.style.width = `${width}px`;
  img.style.height = `${height}px`;
  img.style.objectFit = "cover";
  img.alt = "Image unavailable";

  img.removeAttribute("srcset");
  img.src = brokenImagePlaceholderSrc(width, height);
};
